// src/pages/Digi_Insurance/BulkRegistrationReport.jsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import MainLayout from "../../layouts/MainLayout";
import PageLoader from "../../components/Loader/PageLoader";
import { BASE_URL } from "../Config";
import { useLocation } from "react-router-dom";

const API_BASE = `${BASE_URL}api/Reporting`;

// ── Status badge ───────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    completed:  "bg-success",
    processing: "bg-warning text-dark",
    failed:     "bg-danger",
    queued:     "bg-secondary",
  };
  return (
    <span className={`badge ${map[status?.toLowerCase()] || "bg-secondary"}`}>
      {status}
    </span>
  );
}

// ── Inline alert ───────────────────────────────────────────────────────────
function InlineAlert({ type, message, onClose }) {
  if (!message) return null;
  const icon =
    type === "danger"  ? "bi-x-circle-fill" :
    type === "warning" ? "bi-exclamation-triangle-fill" :
                         "bi-info-circle-fill";
  return (
    <div className={`alert alert-${type} alert-dismissible d-flex align-items-center gap-2 py-2`} role="alert">
      <i className={`bi ${icon}`}></i>
      <span>{message}</span>
      <button type="button" className="btn-close ms-auto" onClick={onClose}></button>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function getLast10Days() {
  const to   = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 10);
  const fmt  = (d) => d.toISOString().split("T")[0];
  return { from: fmt(from), to: fmt(to) };
}

const { from: defaultFrom, to: defaultTo } = getLast10Days();

// ══════════════════════════════════════════════════════════════════════════
export default function BulkRegistrationReport() {

  const location = useLocation();

  // ── Filter state ─────────────────────────────────────────────────────
  const [from,     setFrom]     = useState(defaultFrom);
  const [to,       setTo]       = useState(defaultTo);
  const [status,   setStatus]   = useState("");
  const [client,   setClient]   = useState("");
  const [jobId,    setJobId]    = useState("");

  // ── Data state ───────────────────────────────────────────────────────
  const [data,       setData]       = useState([]);
  const [summary,    setSummary]    = useState({});
  const [pagination, setPagination] = useState({});
  const [page,       setPage]       = useState(1);
  const [statuses,   setStatuses]   = useState([]);

  // ── UI state ─────────────────────────────────────────────────────────
  const [loading,         setLoading]         = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [alert,           setAlert]           = useState(null);

  // ── Load dropdowns once ───────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setDropdownLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/GetBulkJobStatuses`);
        setStatuses(res.data.data || []);
      } catch {
        setAlert({ type: "warning", message: "Could not load filter options." });
      } finally {
        setDropdownLoading(false);
      }
    })();
  }, []);

  // ── Auto-fetch from dashboard navigation ─────────────────────────────
  useEffect(() => {
    if (!location.state) return;

    const { clientName, from: stateFrom, to: stateTo } = location.state;
    const resolvedClient = clientName || "";
    const resolvedFrom   = stateFrom  || defaultFrom;
    const resolvedTo     = stateTo    || defaultTo;

    // Set state for UI
    if (resolvedClient) setClient(resolvedClient);
    if (stateFrom)      setFrom(resolvedFrom);
    if (stateTo)        setTo(resolvedTo);

    // Fetch immediately with resolved values (bypass stale state)
    fetchReport(1, {
      clientOverride: resolvedClient,
      fromOverride:   resolvedFrom,
      toOverride:     resolvedTo,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Date validation ───────────────────────────────────────────────────
  function validateDateRange(f = from, t = to) {
    if (!f && !t) return true;
    if (f && !t)  { setAlert({ type: "warning", message: "Please select To Date."   }); return false; }
    if (!f && t)  { setAlert({ type: "warning", message: "Please select From Date." }); return false; }

    const diff = Math.ceil((new Date(t) - new Date(f)) / 86400000);
    if (new Date(f) > new Date(t)) {
      setAlert({ type: "danger",  message: "From Date cannot be greater than To Date." }); return false;
    }
    if (diff > 10) {
      setAlert({ type: "warning", message: "Maximum allowed date range is 10 days."   }); return false;
    }
    return true;
  }

  // ── Core fetch (accepts optional overrides to avoid stale closure) ────
  async function fetchReport(p = 1, overrides = {}) {
    const {
      clientOverride = null,
      fromOverride   = null,
      toOverride     = null,
    } = overrides;

    const resolvedFrom   = fromOverride   ?? from;
    const resolvedTo     = toOverride     ?? to;
    const resolvedClient = clientOverride ?? client;

    setAlert(null);
    if (!validateDateRange(resolvedFrom, resolvedTo)) return;

    setLoading(true);
    try {
      const params = { PageNumber: p, PageSize: 10 };
      if (resolvedFrom)    params.From       = resolvedFrom;
      if (resolvedTo)      params.To         = resolvedTo;
      if (status)          params.Status     = status;
      if (resolvedClient)  params.ClientName = resolvedClient;
      if (jobId?.trim())   params.JobId      = jobId.trim();

      console.log("REQUEST =>", params);

      const res      = await axios.get(`${API_BASE}/BulkRegistrationReport`, { params });
      const response = res.data;

      if (response.responseCode === "0000") {
        setData(response.data || []);
        setSummary(response.summary || {});
        setPagination(response.pagination || {});
        setPage(p);
        if (!(response.data || []).length) {
          setAlert({ type: "info", message: "No records found." });
        }
      } else {
        setData([]); setSummary({}); setPagination({});
        setAlert({ type: "warning", message: response.message || "No data found." });
      }
    } catch (err) {
      setAlert({
        type: "danger",
        message: err.response?.data?.message || err.response?.data?.title || err.message,
      });
    } finally {
      setLoading(false);
    }
  }

  // ── Reset ─────────────────────────────────────────────────────────────
  function handleReset() {
    setFrom(""); setTo(""); setStatus(""); setClient(""); setJobId("");
    setData([]); setSummary({}); setPagination({}); setPage(1); setAlert(null);
  }

  // ── CSV download ──────────────────────────────────────────────────────
  function downloadCSV() {
    if (!data.length) { setAlert({ type: "warning", message: "No data to download." }); return; }
    const hdrs = ["JobId","Client","Status","TotalUsers","Success","Failed","CreatedAt","CompletedAt"];
    const rows = data.map(d => [
      d.jobId, d.clientName, d.status, d.totalUsers,
      d.successCount, d.failedCount, d.createdAt, d.completedAt,
    ]);
    const csv  = [hdrs, ...rows].map(r => r.join(",")).join("\n");
    const url  = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    Object.assign(document.createElement("a"), { href: url, download: "bulk_registration_report.csv" }).click();
    URL.revokeObjectURL(url);
  }

  // ── Pagination helpers ────────────────────────────────────────────────
  const totalPages   = pagination.totalPages || 1;
  const totalRecords = pagination.totalRecords || 0;

  function buildPageNumbers() {
    if (totalPages <= 1) return [];
    const delta = 2;
    const range = [];
    for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) {
      range.push(i);
    }
    if (range[0] > 1) { range.unshift("..."); range.unshift(1); }
    if (range[range.length - 1] < totalPages) { range.push("..."); range.push(totalPages); }
    return range;
  }

  const pageNumbers = buildPageNumbers();

  // ─────────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <div className="container-fluid px-0">

        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <div className="page-header-responsive mb-3">
          <h4 className="mb-0 fw-bold d-flex align-items-center gap-2 flex-wrap">
            <i className="bi bi-file-earmark-bar-graph text-primary"></i>
            Bulk Registration Report
            {client && (
              <span className={`badge px-3 py-2 text-uppercase ms-1 bg-${client.toLowerCase()}`}>
                {client}
              </span>
            )}
          </h4>
          <button className="btn btn-success btn-sm" onClick={downloadCSV} disabled={!data.length}>
            <i className="bi bi-download me-2"></i>Download CSV
          </button>
        </div>

        {/* ── ALERT ───────────────────────────────────────────────────── */}
        <InlineAlert {...(alert || {})} onClose={() => setAlert(null)} />

        {/* ── SUMMARY CARDS ───────────────────────────────────────────── */}
        <div className="row g-3 mb-3">
          {[
            { label: "Total Jobs",  value: summary.totalJobs,  icon: "bi-briefcase",    color: "text-primary" },
            { label: "Completed",   value: summary.completed,  icon: "bi-check-circle", color: "text-success" },
            { label: "Processing",  value: summary.processing, icon: "bi-arrow-repeat", color: "text-warning" },
            { label: "Total Users", value: summary.totalUsers, icon: "bi-people",       color: "text-info"    },
          ].map(c => (
            <div className="col-6 col-md-3" key={c.label}>
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex align-items-center gap-3 py-3">
                  <div className={`fs-2 ${c.color}`}><i className={`bi ${c.icon}`}></i></div>
                  <div>
                    <div className="text-muted small">{c.label}</div>
                    <div className="fw-bold fs-5">
                      {loading
                        ? <span className="spinner-border spinner-border-sm"></span>
                        : (c.value ?? 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── FILTERS ─────────────────────────────────────────────────── */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">

              <div className="col-6 col-md-2">
                <label className="form-label fw-semibold small mb-1">From</label>
                <input type="date" className="form-control form-control-sm"
                  value={from} onChange={e => setFrom(e.target.value)} />
              </div>

              <div className="col-6 col-md-2">
                <label className="form-label fw-semibold small mb-1">To</label>
                <input type="date" className="form-control form-control-sm"
                  value={to} min={from || undefined} onChange={e => setTo(e.target.value)} />
              </div>

              <div className="col-6 col-md-2">
                <label className="form-label fw-semibold small mb-1">Status</label>
                <select className="form-select form-select-sm" value={status}
                  onChange={e => setStatus(e.target.value)} disabled={dropdownLoading}>
                  <option value="">All</option>
                  {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="col-6 col-md-2">
                <label className="form-label fw-semibold small mb-1">Job ID</label>
                <input className="form-control form-control-sm" placeholder="e.g. JOB-001"
                  value={jobId} onChange={e => setJobId(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && fetchReport(1)} />
              </div>

              <div className="col-12 col-md-3 d-flex gap-2">
                <button className="btn btn-primary btn-sm flex-grow-1"
                  onClick={() => fetchReport(1)} disabled={loading}>
                  {loading
                    ? <span className="spinner-border spinner-border-sm"></span>
                    : <><i className="bi bi-search me-1"></i>Search</>}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={handleReset} title="Clear filters">
                  <i className="bi bi-arrow-counterclockwise me-1"></i>Clear
                </button>
              </div>

            </div>
          </div>
        </div>

        {/* ── TABLE ───────────────────────────────────────────────────── */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            {loading ? (
              <PageLoader label="Loading report…" />
            ) : data.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                No data found. Apply filters and click Search.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover table-bordered mb-0 align-middle small">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Job ID</th>
                      <th>Client</th>
                      <th>Status</th>
                      <th className="text-center">Total</th>
                      <th className="text-center text-success">Success</th>
                      <th className="text-center text-danger">Failed</th>
                      <th>Created At</th>
                      <th>Completed At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((d, idx) => (
                      <tr key={d.jobId}>
                        <td className="text-muted">{(page - 1) * 10 + idx + 1}</td>
                        <td><code className="small">{d.jobId}</code></td>
                        <td>{d.clientName}</td>
                        <td><StatusBadge status={d.status} /></td>
                        <td className="text-center fw-semibold">{d.totalUsers}</td>
                        <td className="text-center text-success fw-semibold">{d.successCount}</td>
                        <td className="text-center text-danger fw-semibold">{d.failedCount}</td>
                        <td className="text-muted">{d.createdAt}</td>
                        <td className="text-muted">{d.completedAt || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── PAGINATION ────────────────────────────────────────────── */}
          {data.length > 0 && (
            <div className="card-footer d-flex flex-wrap justify-content-between align-items-center gap-2 bg-white">

              <small className="text-muted">
                Showing <strong>{(page - 1) * 10 + 1}</strong>–<strong>{Math.min(page * 10, totalRecords)}</strong> of <strong>{totalRecords}</strong> records
              </small>

              <nav>
                <ul className="pagination pagination-sm mb-0 gap-1">

                  {/* First */}
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button className="page-link rounded" onClick={() => fetchReport(1)} disabled={page === 1}>
                      <i className="bi bi-chevron-double-left"></i>
                    </button>
                  </li>

                  {/* Prev */}
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button className="page-link rounded" onClick={() => fetchReport(page - 1)} disabled={page === 1}>
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>

                  {/* Page numbers */}
                  {pageNumbers.map((p, i) =>
                    p === "..." ? (
                      <li key={`ellipsis-${i}`} className="page-item disabled">
                        <span className="page-link border-0 bg-transparent">…</span>
                      </li>
                    ) : (
                      <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
                        <button className="page-link rounded" onClick={() => fetchReport(p)}>
                          {p}
                        </button>
                      </li>
                    )
                  )}

                  {/* Next */}
                  <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
                    <button className="page-link rounded" onClick={() => fetchReport(page + 1)} disabled={page >= totalPages}>
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>

                  {/* Last */}
                  <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
                    <button className="page-link rounded" onClick={() => fetchReport(totalPages)} disabled={page >= totalPages}>
                      <i className="bi bi-chevron-double-right"></i>
                    </button>
                  </li>

                </ul>
              </nav>
            </div>
          )}
        </div>

      </div>

      {/* ── BRAND COLORS ──────────────────────────────────────────────── */}
      <style>{`
        .bg-jazz    { background-color: #221F20 !important; }
        .bg-ufone   { background-color: #F47821 !important; }
        .bg-zong    { background-color: #8CC63E !important; }
        .bg-telenor { background-color: #007AD0 !important; }
        .page-link  { min-width: 32px; text-align: center; }
      `}</style>
    </MainLayout>
  );
}